import React, { useState } from 'react';
import axios from 'axios';
import { Plane, Calendar, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEO, { breadcrumbSchema } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Flights = () => {
  const [searchData, setSearchData] = useState({
    from: '',
    to: '',
    departure: '',
    return_date: '',
    passengers: '1',
    trip_type: 'oneway'
  });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  const popularRoutes = [
    { from: 'Mumbai', to: 'Dubai', price: '\u20b918,500' },
    { from: 'Delhi', to: 'Bangkok', price: '\u20b922,000' },
    { from: 'Mumbai', to: 'Singapore', price: '\u20b928,000' },
    { from: 'Delhi', to: 'London', price: '\u20b945,000' },
    { from: 'Mumbai', to: 'New York', price: '\u20b965,000' },
    { from: 'Delhi', to: 'Bali', price: '\u20b932,000' }
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in your contact details');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        inquiry_type: 'flight',
        subject: `Flight Inquiry: ${searchData.from} to ${searchData.to}`,
        message: `Looking for ${searchData.trip_type} flight`,
        details: searchData
      });
      toast.success('Flight inquiry submitted! Our team will share the best deals shortly.');
      setSearchData({ from: '', to: '', departure: '', return_date: '', passengers: '1', trip_type: 'oneway' });
      setFormData({ name: '', email: '', phone: '' });
    } catch (err) {
      toast.error('Failed to submit. Please call us at +91 90169 30091');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Flight Booking from Surat - Best Deals on Domestic & International Flights"
        description="Book flights from Surat with Varni Visa. Best deals on domestic and international tickets — Dubai, Bangkok, Singapore, London, New York. Free quote in 30 mins."
        keywords="Flight Booking, Flight Booking Surat, Cheap Flights, International Flights from India"
        path="/flights"
        schema={breadcrumbSchema([{name: 'Home', path: '/'}, {name: 'Flight Booking', path: '/flights'}])}
      />
      <section 
        className="relative h-[350px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(45, 141, 180, 0.85), rgba(247, 148, 93, 0.75)), url('https://images.pexels.com/photos/4922085/pexels-photo-4922085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Flight Booking</h1>
          <p className="text-xl">Best deals on domestic and international flights</p>
        </div>
      </section>

      {/* Search Form */}
      <section className="py-12 -mt-20 relative z-10">
        <div className="container mx-auto px-4">
          <Card className="max-w-5xl mx-auto shadow-2xl">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Plane className="text-[#2D8DB4]" />
                Search Flights
              </h2>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="from">From *</Label>
                    <Input
                      id="from"
                      placeholder="Departure city"
                      required
                      value={searchData.from}
                      onChange={(e) => setSearchData({ ...searchData, from: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="to">To *</Label>
                    <Input
                      id="to"
                      placeholder="Destination city"
                      required
                      value={searchData.to}
                      onChange={(e) => setSearchData({ ...searchData, to: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="departure">Departure *</Label>
                    <Input
                      id="departure"
                      type="date"
                      required
                      value={searchData.departure}
                      onChange={(e) => setSearchData({ ...searchData, departure: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="passengers">Passengers</Label>
                    <Select value={searchData.passengers} onValueChange={(v) => setSearchData({ ...searchData, passengers: v })}>
                      <SelectTrigger id="passengers">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Your Contact Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="cname">Name *</Label>
                      <Input
                        id="cname"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="cemail">Email</Label>
                      <Input
                        id="cemail"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="cphone">Phone *</Label>
                      <Input
                        id="cphone"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#2D8DB4] hover:bg-[#247a9a]" size="lg" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Get Best Flight Deals'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Popular Routes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularRoutes.map((route, idx) => (
              <Card key={idx} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-2xl font-bold">{route.from}</p>
                      <p className="text-sm text-gray-500">From</p>
                    </div>
                    <Plane className="text-[#2D8DB4]" size={32} />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{route.to}</p>
                      <p className="text-sm text-gray-500">To</p>
                    </div>
                  </div>
                  <div className="border-t pt-4 flex justify-between items-center">
                    <span className="text-sm text-gray-500">Starting from</span>
                    <span className="text-xl font-bold text-[#F7945D]">{route.price}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Flights;
